<template>
  <section>
    <v-col>
      <v-row class="ma-0 mb-4">
        <v-btn color="secondary" class="text-capitalize" @click="onBack">
          {{ $translate('BACK') }}
        </v-btn>
      </v-row>

      <h2>{{ $translate('SUBSCRIPTION_STEP3') }}</h2>

      <v-form ref="SubscriptionDeliveryPickUpForm">
        <DeliveryPickUpEvent
          class="mb-4"
          :eventIds="eventIds"
          :initialSelection="deliveryPickUpEvent"
          :subscriptionProducts="products"
          :initialDeliveryFees="deliveryFeeItems"
          @change="onDeliveryPickUpEventChange"
          @setCountryCodes="setCountryCodes"
          @change:delivery-fee="onDeliveryFeeChange"
        />

        <DeliveryAlert
          v-if="showDeliveryInfo"
          :alerts="deliveryInfoList"
          :improvements="improvements"
          class="mb-6"
        />

        <PickupAlert :visible="isPickup" :pickupFee="pickupFee" />

        <DeliveryPickUpUserInfo
          class="mb-6"
          :event="deliveryPickUpEvent"
          :user="userInfo"
          :countryCodes="countryCodes"
          :products="products"
          @loading="onLoadingUserInfo"
          @change="onUserInfoChange"
        />

        <DeliveryPickUpDateTime
          :note="deliveryNote"
          :deliveryDateTimeInfo="deliveryDateTimeInfo"
          :event="deliveryPickUpEvent"
          :user="userInfo"
          :subscriptionProducts="products"
          :initialStartDate="startDate"
          :initialStartTime="startTimeObj"
          @start-date-change="onStartDateChange"
          @start-time-change="onStartTimeChange"
          @setTicketList="setTicketList"
        />

        <DeliveryPickUpTickets :ticketsList="listOfTickets" />

        <!-- PaymentMethod -->
        <v-row v-if="hasMultiPaymentMethods">
          <v-col cols="12" md="10">
            <h3 class="my-4">
              {{ $translate('SELECT_PAYMENT_METHOD') }}
            </h3>
            <!-- Payment method selection -->
            <PaymentMethodSelect
              v-model="selectedPaymentMethod"
              :items="paymentMethods"
              :ref="paymentMethodSelectElId"
              :id="paymentMethodSelectElId"
            />
          </v-col>
        </v-row>

        <!-- Delivery error / price info -->
        <v-row v-if="showDeliveryInfo" class="ma-0 mt-3" dense>
          <v-col cols="12" class="px-0">
            <DeliveryAlert
              :alerts="deliveryInfoList"
              :improvements="improvements"
            />
          </v-col>
        </v-row>

        <PickupAlert :visible="isPickup" :pickupFee="pickupFee" />
      </v-form>

      <v-row class="ma-0 mt-4" justify="end">
        <v-btn
          color="primary"
          class="text-capitalize primary-font-color"
          :disabled="nextBtnDisabled"
          :loading="nextBtnLoading"
          @click="onNext"
        >
          {{ $translate('NEXT') }}
        </v-btn>
      </v-row>

      <!-- Error dialog -->
      <ErrorDialog
        v-if="showErrorDialog"
        :dialog="showErrorDialog"
        :errorMessage="errorMessage"
      />
    </v-col>
  </section>
</template>

<script>
import doRequest from '@/api/dssRequest';
import DeliveryPickUpEvent from '@/components/generalComponents/deliveryPickUp/DeliveryPickUpEvent';
import DeliveryPickUpDateTime from '@/components/generalComponents/deliveryPickUp/DeliveryPickUpDateTime';
import DeliveryPickUpUserInfo from '@/components/generalComponents/deliveryPickUp/DeliveryPickUpUserInfo';
import DeliveryPickUpTickets from '@/components/generalComponents/deliveryPickUp/DeliveryPickUpTickets';
import DeliveryAlert from '@/components/generalComponents/deliveryPickUp/DeliveryAlert';
import PickupAlert from '@/components/generalComponents/deliveryPickUp/PickupAlert';
import PickupFeeJS from '@/objects/PickupFee';
import utility, { scrollToElement } from '@/shared/utility';
import PaymentMethodSelect from '@/components/booking/PaymentMethodSelect';
import PaymentMethodObj from '@/objects/PaymentMethod';
import ErrorDialog from '@/components/generalComponents/uiComponents/ErrorDialog.vue';

export default {
  name: 'SubscriptionDeliveryPickUp',
  components: {
    DeliveryPickUpEvent,
    DeliveryPickUpDateTime,
    DeliveryPickUpUserInfo,
    DeliveryPickUpTickets,
    DeliveryAlert,
    PickupAlert,
    PaymentMethodSelect,
    ErrorDialog,
  },
  data() {
    return {
      endDate: '',

      // Delivery variables
      deliveryNote: '',
      deliveryDateTimeInfo: {},
      countryCodes: [],
      showDeliveryInfo: false,
      deliveryPossible: false,
      deliveryText: '',
      deliveryInfoList: [],
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),

      userInfoLoading: false,
      nextBtnLoading: false,
      improvements: [],

      deliveryFeeItems: [],
      selectedDeliveryFeeItem: {},
      listOfTickets: {},

      pickupFee: null,
      paymentMethods: [],
      selectedPaymentMethod: undefined,
      paymentMethodSelectElId: 'paymentMethodSelect',
      showErrorDialog: false,
      errorMessage: '',
    };
  },
  computed: {
    eventIds() {
      const event = this.$store.getters['subscription/event'];
      if (event != null && event.EventId != null) {
        return [event.EventId];
      }

      return [];
    },
    products() {
      return this.getSelectedProducts();
    },
    deliveryPickUpEvent() {
      return this.$store.getters['subscription/deliveryPickUpEvent'];
    },
    deliveryPickUpOrderId() {
      return this.$store.getters['subscription/deliveryPickUpOrderId'];
    },
    startDate() {
      return this.$store.getters['subscription/startDate'];
    },
    startTimeObj() {
      return this.$store.getters['subscription/startTimeObj'];
    },
    userInfo() {
      let userInfoTmp = {};

      const userInSubscription = this.$store.getters['subscription/userInfo'];
      const userInAuth = this.$store.getters['auth/user'];
      if (
        userInSubscription != null &&
        Object.keys(userInSubscription).length > 0
      ) {
        userInfoTmp = { ...userInSubscription };
      } else if (userInAuth != null && Object.keys(userInAuth).length > 0) {
        userInfoTmp = { ...userInAuth };
        this.$store.commit('subscription/setUserInfo', userInfoTmp);
      }

      return userInfoTmp;
    },
    userIsLoggedIn() {
      return this.$store.getters['auth/isValid'];
    },
    userObjectId() {
      return this.userIsLoggedIn
        ? this.$store.getters['auth/userName']
        : this.$store.getters['auth/tmpUserId'];
    },
    nextBtnDisabled() {
      if (this.isDelivery && !this.deliveryPossible) {
        return true;
      }
      if (
        this.isPickup &&
        this.pickupFee != null &&
        !this.pickupFee.isPossible()
      ) {
        return true;
      }
      if (this.userInfoLoading) {
        return true;
      }
      return false;
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
    isDelivery() {
      return (
        this.deliveryPickUpEvent != null &&
        (this.deliveryPickUpEvent.Type === 'Delivery' ||
          this.deliveryPickUpEvent.Type === 'Shiplee')
      );
    },
    isPickup() {
      return (
        this.deliveryPickUpEvent != null &&
        this.deliveryPickUpEvent.Type === 'PickUp'
      );
    },
    hasMultiPaymentMethods() {
      return this.paymentMethods != null && this.paymentMethods.length > 1;
    },
  },
  methods: {
    getSelectedProducts() {
      const productsInStore = this.$store.getters['subscription/products'];
      if (productsInStore == null) {
        return [];
      }

      const productsTmp = [];

      productsInStore.forEach((product) => {
        if (product.ItemId != null) {
          productsTmp.push({
            ProductId: product.ProductId,
            ItemId: product.ItemId,
            Quantity: product.Quantity,
            OptionSelection: product.OptionSelection,
          });
        }
      });

      return productsTmp;
    },
    getUserInfo() {
      return {
        Name: `${this.userInfo.firstName} ${this.userInfo.lastName}` || '',
        Email: this.userInfo.email || '',
        PhoneNumber: this.userInfo.phoneNo || '',
        Street: this.userInfo.street || '',
        Nr: this.userInfo.houseNo || '',
        PostalCode: this.userInfo.postalCode || '',
        City: this.userInfo.city || '',
        Country: this.userInfo.country || '',
        CountryCode: this.userInfo.countryCode || '',
        Latitude:
          this.userInfo.latitude == null ? -200 : this.userInfo.latitude,
        Longitude:
          this.userInfo.longitude == null ? -200 : this.userInfo.longitude,
        Note: this.userInfo.note || '',
      };
    },
    setCountryCodes(countryCodes) {
      this.countryCodes = countryCodes;
    },
    onDeliveryPickUpEventChange(eventUpdate) {
      this.$store.commit('subscription/setDeliveryPickUpEvent', eventUpdate);
      this.getDeliveryDetails();
      this.getPickupFee();
      this.getPaymentMethods();
    },
    onStartDateChange(date) {
      this.$store.commit('subscription/setStartDate', date);
    },
    onStartTimeChange(timeObj) {
      this.$store.commit('subscription/setStartTimeObj', timeObj);
    },
    onUserInfoChange(info) {
      this.$store.commit('subscription/setUserInfo', info);
      this.getDeliveryDetails();
    },
    onLoadingUserInfo(loading) {
      this.userInfoLoading = loading;
    },
    onBack() {
      this.$emit('back');
    },
    onNext() {
      this.nextBtnLoading = true;

      if (!this.$refs.SubscriptionDeliveryPickUpForm.validate()) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        this.nextBtnLoading = false;
        return;
      }

      if (!this.validatePaymentMethod()) {
        this.scrollToPaymentMethod();
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        this.nextBtnLoading = false;
        return;
      }

      // If event already reserved, cancel existing reservation first before reserve again
      if (
        this.deliveryPickUpOrderId != null &&
        this.deliveryPickUpOrderId.trim().length > 0
      ) {
        this.cancelReservation()
          .then(() => {
            this.$store.commit('subscription/setDeliveryPickUpOrderId', '');
            this.reserve();
          })
          .catch(() => {
            // Cancellation failed, reserve anyway
            this.$store.commit('subscription/setDeliveryPickUpOrderId', '');
            this.reserve();
          });
      } else {
        this.reserve();
      }
    },
    reserve() {
      const userInfoPayload = this.getUserInfo();

      const payload = {
        OrderId: '',
        EventId: this.deliveryPickUpEvent.EventId,
        StartTime: this.startTimeObj.StartTime,
        EndTime: '',
        StartDate: this.startDate,
        EndDate: this.endDate,
        StartBatchGroupId: this.startTimeObj.BatchGroupId,
        EndBatchGroupId: this.startTimeObj.BatchGroupId,
        UserInfo: userInfoPayload,
        SubscriptionProducts: this.products,
      };

      payload.Note = userInfoPayload.Note;
      payload.SpecialRequests = [];
      payload.Tickets = {};

      for (const item of Object.values(this.listOfTickets)) {
        if (item.Amount > 0) payload.Tickets[item.Id] = item.Amount;
      }

      doRequest(
        (response) => {
          this.nextBtnLoading = false;

          if (response.data.MainOrder != null) {
            this.$store.commit(
              'subscription/setDeliveryPickUpOrderId',
              response.data.MainOrder.OrderId
            );
            this.$store.commit(
              'subscription/setSelectedPaymentMethod',
              this.selectedPaymentMethod
            );
            this.$emit('next');
          } else {
            this.onReserveError();
          }
        },
        (error) => {
          this.nextBtnLoading = false;
          this.onReserveError(error);
        },
        'reserve',
        this.userObjectId,
        payload
      );
    },
    onReserveError(error = null) {
      if (
        error != null &&
        error.data != null &&
        error.data.ErrorCode === 'E_PO_006'
      ) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('E_PO_006_SUBSCRIPTION_PRODUCT'),
          color: 'error',
        });
      } else {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('FAILED_RESERVING_TICKETS'),
          color: 'error',
        });
      }
    },
    cancelReservation() {
      const payload = {
        OrderId: this.deliveryPickUpOrderId,
        EventId: this.deliveryPickUpEvent.EventId,
      };

      return new Promise((resolve, reject) => {
        doRequest(
          resolve,
          reject,
          'cancelReservation',
          this.userObjectId,
          payload
        );
      });
    },
    getDeliveryDetails() {
      this.deliveryFeeItems = [];
      this.selectedDeliveryFeeItem = {};
      this.clearDeliveryFeeDetails();
      this.$store.commit('subscription/setDeliveryDetails', {});

      const userInfo = this.getUserInfo();
      if (!userInfo.PostalCode || !this.isDelivery) {
        return;
      }

      const todayDate = utility.getCurrentDateDMY();

      const payload = {
        UserInfo: userInfo,
        StartDate: todayDate,
        Reservations: {},
        SubscriptionProducts: this.products,
        OneClickBooking: true,
      };

      const cartevent = this.$store.getters['shoppingCart/orders'];

      if (cartevent != null) {
        cartevent.forEach((reservation) => {
          payload.Reservations[reservation.EventId] = [reservation.OrderId];
        });
      }

      doRequest(
        (response) => {
          if (response.data.DeliveryFees != null) {
            this.deliveryFeeItems = [...response.data.DeliveryFees];
          }
        },
        () => {
          this.showDeliveryInfo = true;
          this.deliveryInfoList.push({
            type: 'error',
            text: this.$translate('NO_DELIVERY_FOR_POSTAL_CODE'),
          });
          this.deliveryPossible = false;
        },
        'getDeliveryDetailsV2',
        this.$store.getters['auth/userObjectId'],
        payload
      );
    },
    getDeliveryFeeText(deliveryDetails) {
      let deliveryPrice = 0;
      let priceSuffix = '';
      if (this.isProfUser) {
        deliveryPrice =
          deliveryDetails.ResultSet.Price - deliveryDetails.ResultSet.VatPrice;
        priceSuffix = ` (${this.$translate('EXCL_VAT')})`;
      } else {
        deliveryPrice = deliveryDetails.ResultSet.Price;
      }

      return `${this.$translate('DELIVERY_FEE')}: ${this.formatter.format(
        deliveryPrice
      )}${priceSuffix}`;
    },
    clearDeliveryFeeDetails() {
      this.deliveryNote = '';
      this.showDeliveryInfo = false;
      this.deliveryInfoList = [];
      this.improvements = [];
      this.deliveryDateTimeInfo = {};
    },
    onDeliveryFeeChange(deliveryFeeSelection) {
      if (this.isDelivery) {
        this.clearDeliveryFeeDetails();
        this.selectedDeliveryFeeItem = { ...deliveryFeeSelection };
        this.parseDeliveryFeeDetails();
      }
    },
    parseDeliveryFeeDetails() {
      if (this.selectedDeliveryFeeItem.DeliveryPossible) {
        this.deliveryPossible = true;
        this.deliveryNote = this.selectedDeliveryFeeItem.Note;
        this.improvements = this.selectedDeliveryFeeItem.Improvements;
        this.deliveryDateTimeInfo = this.selectedDeliveryFeeItem.DateTimeInfo;
        const deliveryEvent = this.deliveryPickUpEvent;
        deliveryEvent.EventId = this.selectedDeliveryFeeItem.EventId;
        this.$store.commit(
          'subscription/setDeliveryPickUpEvent',
          deliveryEvent
        );
        this.$store.commit(
          'subscription/setDeliveryDetails',
          this.selectedDeliveryFeeItem
        );
      } else {
        if (
          this.selectedDeliveryFeeItem.ResultSet != null &&
          this.selectedDeliveryFeeItem.ResultSet.Reasons != null
        ) {
          this.selectedDeliveryFeeItem.ResultSet.Reasons.forEach((reason) => {
            switch (reason.ErrorCode) {
              case 'E_DEL_000':
                // Invalid postal code
                this.deliveryInfoList.push({
                  type: 'error',
                  text: this.$translate('INVALID_POSTAL_CODE'),
                });
                break;
              case 'E_DEL_001':
                // Postal code not reachable
                this.deliveryInfoList.push({
                  type: 'error',
                  text: this.$translate('NO_DELIVERY_FOR_POSTAL_CODE'),
                });
                break;
              case 'E_DEL_002': {
                // Minimum value not reached
                const text = this.$translate(
                  'DELIVERY_NO_ENOUGH_PRODUCTS'
                ).replace(
                  'price',
                  this.formatter.format(reason.AdditionalAmountRequired)
                );
                this.deliveryInfoList.push({
                  type: 'error',
                  text,
                });
                break;
              }
              default:
                this.deliveryInfoList.push({
                  type: 'error',
                  text: this.$translate('NO_DELIVERY_FOR_POSTAL_CODE'),
                });
                break;
            }
          });
        } else {
          this.deliveryInfoList.push({
            type: 'error',
            text: this.$translate('NO_DELIVERY_FOR_POSTAL_CODE'),
          });
        }
        this.deliveryPossible = false;
      }

      this.showDeliveryInfo = true;
    },
    setTicketList(tickets) {
      this.listOfTickets = tickets;
    },
    clearPickupFee() {
      this.pickupFee = null;
      this.$store.commit('subscription/setPickupFee', this.pickupFee);
    },
    async getPickupFee() {
      if (!this.isPickup) {
        this.clearPickupFee();
        return;
      }

      this.pickupFee = new PickupFeeJS(this.deliveryPickUpEvent.EventId);

      try {
        await this.pickupFee.load({}, '', this.products);
        this.$store.commit('subscription/setPickupFee', this.pickupFee);
      } catch (error) {
        this.clearPickupFee();
      }
    },
    getPaymentMethods() {
      const deliveryPickupEventId = this.deliveryPickUpEvent.EventId;

      PaymentMethodObj.getPaymentMethods(
        this.userObjectId,
        {},
        deliveryPickupEventId,
        undefined,
        this.products,
        true
      )
        .then((methods) => {
          this.paymentMethods = [...methods];
          if (this.paymentMethods.length === 1) {
            [this.selectedPaymentMethod] = this.paymentMethods;
          }
        })
        .catch((error) => {
          console.log('An error occurred in getPaymentMethods');
          let errorMsg = '';
          if (error.data.ErrorCode === 'E_PAYM_001') {
            this.showErrorDialog = true;
            this.errorMessage = this.$translate('E_PAYM_001_NO_PAYMENT');
            errorMsg = this.$translate('PAYMENT_METHOD_NOT_AVAILABLE');
            window.EventBus.fire('activateSnackBar', {
              text: errorMsg,
              color: 'error',
            });
          }
          this.paymentMethods = [];
        });
    },
    validatePaymentMethod() {
      const paymentRef = this.$refs[this.paymentMethodSelectElId];
      console.log(paymentRef);
      if (paymentRef != null && !paymentRef.validate()) {
        return false;
      }

      return true;
    },
    scrollToPaymentMethod() {
      scrollToElement(`#${this.paymentMethodSelectElId}`);
    },
  },
};
</script>
